import * as TTL from "../../../Stores/TimeTrackerConstants.js";
import {
    isRightDrawerMemberNotShiftedIn,
    isRightDrawerMemberShiftedOut,
} from "./useRightDrawerMemberShiftUi.js";

/**
 * Parse a workline/profile datetime the same way the red-pause badge does
 * (naive strings are treated as UTC).
 *
 * @param {string} utcTimeString
 * @returns {number}
 */
/**
 * Normalize API datetimes for JS parsing (naive → UTC, trim Laravel microseconds).
 *
 * @param {string|null|undefined} utcTimeString
 * @returns {string|null}
 */
export function normalizeWorklineDateString(utcTimeString) {
    if (utcTimeString == null || utcTimeString === "") {
        return null;
    }

    let normalized = String(utcTimeString);
    if (normalized.slice(-1) !== "Z" && !normalized.includes("+")) {
        normalized = `${normalized}Z`;
    }

    return normalized.replace(/\.(\d{3})\d+Z$/i, ".$1Z");
}

function timestampMs(utcTimeString) {
    const normalized = normalizeWorklineDateString(utcTimeString);
    if (!normalized) {
        return Number.NaN;
    }

    return new Date(normalized).getTime();
}

/**
 * @param {string|null|undefined} utcTimeString
 * @returns {Date|null}
 */
export function parseWorklineUtcDate(utcTimeString) {
    const normalized = normalizeWorklineDateString(utcTimeString);
    if (!normalized) {
        return null;
    }

    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
}

/**
 * Later of two ISO/datetime strings. Used so a live profile update
 * (disconnect at 12:50) wins over a stale workline last-event, and vice versa.
 *
 * @param {string|null|undefined} a
 * @param {string|null|undefined} b
 * @returns {string|null}
 */
export function laterTimestamp(a, b) {
    if (!a) {
        return b ?? null;
    }
    if (!b) {
        return a;
    }
    return timestampMs(a) >= timestampMs(b) ? a : b;
}

/**
 * Same fields a refresh of `/shift-timer/shift` puts on the workline row.
 * Live updates must patch the row first; this helper does not merge statuses.
 *
 * first_event_timestamp = first clock-in today (presence only).
 * The red pause badge uses the last shift event, not the first clock-in.
 *
 * @param {object|null|undefined} row
 * @param {object|null|undefined} profile
 */
export function resolveWorklineShiftFromRow(row, profile) {
    const hasWorklineShiftToday = Boolean(row?.first_event_timestamp);

    if (!hasWorklineShiftToday) {
        return {
            shiftStatus: TTL.Not_Shifted_In,
            recentShiftAt: null,
        };
    }

    return {
        shiftStatus: row.shift_status ?? row.event_type ?? profile?.shift_status,
        recentShiftAt: laterTimestamp(
            profile?.recent_shift_event_at,
            row.last_event_timestamp ?? row.recent_shift_event_at,
        ),
    };
}

/**
 * @param {string|number|null|undefined} userProfileId
 * @param {{ usr_profile_logged_time?: object[] }} appState
 * @param {{ getUserProfileById: (id: unknown) => object|undefined, getUserProfile: (id: unknown) => object|undefined }} userStore
 * @returns {{ shift_status: string, recent_shift_event_at: string|null }|null}
 */
export function buildMemberShiftContext(userProfileId, appState, userStore) {
    if (userProfileId == null || userProfileId === "") {
        return null;
    }

    const id = Number(userProfileId);
    if (!Number.isFinite(id)) {
        return null;
    }

    const profile =
        userStore.getUserProfileById(id) ?? userStore.getUserProfile(id);

    const row = appState.usr_profile_logged_time?.find(
        (entry) =>
            Number(entry?.user_profile_id ?? entry?.employee_id) === id,
    );

    const { shiftStatus, recentShiftAt } = resolveWorklineShiftFromRow(
        row ?? null,
        profile ?? {},
    );

    return {
        shift_status: shiftStatus,
        recent_shift_event_at: recentShiftAt,
    };
}

/**
 * @param {object|null|undefined} member
 * @param {{ isShiftNotShiftedIn?: (status: unknown) => boolean, isShiftOut: (status: unknown) => boolean }} tt
 */
export function resolveMemberShiftAvatarState(member, tt) {
    if (!member) {
        return {
            isNotShiftedIn: false,
            isShiftedOut: false,
            isInactive: false,
        };
    }

    const isNotShiftedIn = isRightDrawerMemberNotShiftedIn(member, tt);
    const isShiftedOut = isRightDrawerMemberShiftedOut(member, tt);

    return {
        isNotShiftedIn,
        isShiftedOut,
        isInactive: isShiftedOut,
    };
}

/**
 * CSS classes for team-dashboard avatars (matches workline rail).
 *
 * @param {{ isNotShiftedIn?: boolean, isShiftedOut?: boolean }|null|undefined} state
 */
export function shiftAvatarClassNames(state) {
    return {
        "workline-member-offline": Boolean(state?.isNotShiftedIn),
        "workline-member-shift-out": Boolean(state?.isShiftedOut),
    };
}
